import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getObjectiveStats, getClassrooms } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Target, BookOpen, Users, CheckCircle, 
  Keyboard, HelpCircle, TrendingUp
} from 'lucide-react';

const ObjectivesPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes] = await Promise.all([
          getObjectiveStats()
        ]);
        setStats(statsRes.data);
      } catch (error) {
        toast.error('Failed to load objective stats');
        navigate('/teacher');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const getMasteryColor = (score) => {
    if (score >= 80) return 'text-neon-green';
    if (score >= 60) return 'text-neon-yellow';
    return 'text-neon-red';
  };

  const getMasteryBg = (score) => {
    if (score >= 80) return 'bg-neon-green';
    if (score >= 60) return 'bg-neon-yellow';
    return 'bg-neon-red';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const overview = stats?.overview || {};
  const quizByObjective = stats?.quiz_by_objective || [];
  const quizByStudent = stats?.quiz_by_student || [];
  const typingByObjective = stats?.typing_by_objective || [];
  const typingByStudent = stats?.typing_by_student || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/teacher')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold">Objective Tracking</h1>
            <p className="text-text-secondary text-sm">Monitor ELA objective performance</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="card-glass p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Exercises</p>
                <p className="font-heading text-xl font-bold">{overview.total_exercises || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Completions</p>
                <p className="font-heading text-xl font-bold">{overview.total_completions || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-status-warning/20 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-status-warning" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Questions Asked</p>
                <p className="font-heading text-xl font-bold">{overview.total_questions_asked || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-status-success/20 flex items-center justify-center">
                <Target className="w-5 h-5 text-status-success" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Questions Correct</p>
                <p className="font-heading text-xl font-bold">{overview.total_questions_correct || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="card-glass p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-text-muted text-xs uppercase">Overall Quiz %</p>
                <p className={`font-heading text-xl font-bold ${getMasteryColor(overview.overall_quiz_percent || 0)}`}>
                  {overview.overall_quiz_percent || 0}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="comprehension" className="space-y-6">
          <TabsList className="bg-background-paper border border-white/10">
            <TabsTrigger 
              value="comprehension" 
              className="data-[state=active]:bg-secondary data-[state=active]:text-white"
              data-testid="comprehension-tab"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Comprehension Stats
            </TabsTrigger>
            <TabsTrigger 
              value="typing" 
              className="data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="typing-tab"
            >
              <Keyboard className="w-4 h-4 mr-2" />
              Typing Stats
            </TabsTrigger>
          </TabsList>

          {/* COMPREHENSION TAB */}
          <TabsContent value="comprehension">
            <Tabs defaultValue="by-objective" className="space-y-4">
              <TabsList className="bg-background-surface border border-white/10">
                <TabsTrigger 
                  value="by-objective" 
                  className="data-[state=active]:bg-white/10"
                  data-testid="comprehension-by-objective-tab"
                >
                  <Target className="w-4 h-4 mr-2" />
                  By Objective
                </TabsTrigger>
                <TabsTrigger 
                  value="by-student" 
                  className="data-[state=active]:bg-white/10"
                  data-testid="comprehension-by-student-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  By Student
                </TabsTrigger>
              </TabsList>

              {/* Comprehension by Objective */}
              <TabsContent value="by-objective">
                <Card className="card-glass overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-heading font-bold flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-secondary" />
                      Quiz Questions by Objective
                    </h3>
                    <p className="text-text-muted text-sm">Track comprehension performance for each ELA standard</p>
                  </div>
                  
                  {quizByObjective.length === 0 ? (
                    <div className="p-12 text-center">
                      <HelpCircle className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">No quiz data yet. Students need to complete exercises first.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-text-secondary">Objective Code</TableHead>
                          <TableHead className="text-text-secondary">Description</TableHead>
                          <TableHead className="text-text-secondary text-center">Questions Asked</TableHead>
                          <TableHead className="text-text-secondary text-center">Correct</TableHead>
                          <TableHead className="text-text-secondary text-center">% Correct</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quizByObjective.map((obj) => (
                          <TableRow key={obj.code} className="border-white/5 hover:bg-white/5">
                            <TableCell className="font-mono text-primary font-bold">
                              {obj.code}
                            </TableCell>
                            <TableCell className="text-text-secondary text-sm max-w-xs truncate">
                              {obj.description}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {obj.questions_asked}
                            </TableCell>
                            <TableCell className="text-center font-mono text-status-success">
                              {obj.questions_correct}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getMasteryBg(obj.percent_correct)}`}
                                    style={{ width: `${obj.percent_correct}%` }}
                                  />
                                </div>
                                <span className={`font-mono font-bold ${getMasteryColor(obj.percent_correct)}`}>
                                  {obj.percent_correct}%
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              {/* Comprehension by Student */}
              <TabsContent value="by-student">
                <Card className="card-glass overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-heading font-bold flex items-center gap-2">
                      <Users className="w-5 h-5 text-secondary" />
                      Quiz Performance by Student
                    </h3>
                    <p className="text-text-muted text-sm">Track each student's comprehension performance</p>
                  </div>
                  
                  {quizByStudent.length === 0 ? (
                    <div className="p-12 text-center">
                      <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">No student quiz data yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-text-secondary">Student</TableHead>
                          <TableHead className="text-text-secondary text-center">Quizzes Taken</TableHead>
                          <TableHead className="text-text-secondary text-center">Questions Asked</TableHead>
                          <TableHead className="text-text-secondary text-center">Correct</TableHead>
                          <TableHead className="text-text-secondary text-center">% Correct</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quizByStudent.map((student) => (
                          <TableRow key={student.student_id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="font-medium">
                              {student.name}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {student.quizzes_completed}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {student.questions_asked}
                            </TableCell>
                            <TableCell className="text-center font-mono text-status-success">
                              {student.questions_correct}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${getMasteryBg(student.percent_correct)}`}
                                    style={{ width: `${student.percent_correct}%` }}
                                  />
                                </div>
                                <span className={`font-mono font-bold ${getMasteryColor(student.percent_correct)}`}>
                                  {student.percent_correct}%
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* TYPING TAB */}
          <TabsContent value="typing">
            <Tabs defaultValue="by-objective" className="space-y-4">
              <TabsList className="bg-background-surface border border-white/10">
                <TabsTrigger 
                  value="by-objective" 
                  className="data-[state=active]:bg-white/10"
                  data-testid="typing-by-objective-tab"
                >
                  <Target className="w-4 h-4 mr-2" />
                  By Objective
                </TabsTrigger>
                <TabsTrigger 
                  value="by-student" 
                  className="data-[state=active]:bg-white/10"
                  data-testid="typing-by-student-tab"
                >
                  <Users className="w-4 h-4 mr-2" />
                  By Student
                </TabsTrigger>
              </TabsList>

              {/* Typing by Objective */}
              <TabsContent value="by-objective">
                <Card className="card-glass overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-heading font-bold flex items-center gap-2">
                      <Keyboard className="w-5 h-5 text-primary" />
                      Typing Performance by Objective
                    </h3>
                    <p className="text-text-muted text-sm">Track typing performance for each ELA standard</p>
                  </div>
                  
                  {typingByObjective.length === 0 ? (
                    <div className="p-12 text-center">
                      <Keyboard className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">No typing data yet.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-text-secondary">Objective</TableHead>
                          <TableHead className="text-text-secondary text-center">Completions</TableHead>
                          <TableHead className="text-text-secondary text-center">Students</TableHead>
                          <TableHead className="text-text-secondary text-center">Avg Accuracy</TableHead>
                          <TableHead className="text-text-secondary text-center">Avg WPM</TableHead>
                          <TableHead className="text-text-secondary text-center">Total Words</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {typingByObjective.map((obj) => (
                          <TableRow key={obj.code} className="border-white/5 hover:bg-white/5">
                            <TableCell>
                              <span className="font-mono text-primary font-bold">{obj.code}</span>
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {obj.times_completed}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {obj.unique_students}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-mono font-bold ${getMasteryColor(obj.avg_accuracy)}`}>
                                {obj.avg_accuracy}%
                              </span>
                            </TableCell>
                            <TableCell className="text-center font-mono text-secondary">
                              {obj.avg_wpm}
                            </TableCell>
                            <TableCell className="text-center font-mono text-text-muted">
                              {obj.total_words_typed.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>

              {/* Typing by Student */}
              <TabsContent value="by-student">
                <Card className="card-glass overflow-hidden">
                  <div className="p-4 border-b border-white/10">
                    <h3 className="font-heading font-bold flex items-center gap-2">
                      <Users className="w-5 h-5 text-secondary" />
                      Typing Performance by Student
                    </h3>
                    <p className="text-text-muted text-sm">Track each student's typing performance</p>
                  </div>
                  
                  {typingByStudent.length === 0 ? (
                    <div className="p-12 text-center">
                      <Users className="w-12 h-12 text-text-muted mx-auto mb-4" />
                      <p className="text-text-secondary">No student typing data yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                          <TableHead className="text-text-secondary">Student</TableHead>
                          <TableHead className="text-text-secondary text-center">Exercises</TableHead>
                          <TableHead className="text-text-secondary text-center">Avg Accuracy</TableHead>
                          <TableHead className="text-text-secondary text-center">Avg WPM</TableHead>
                          <TableHead className="text-text-secondary text-center">Total Words</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {typingByStudent.map((student) => (
                          <TableRow key={student.student_id} className="border-white/5 hover:bg-white/5">
                            <TableCell className="font-medium">
                              {student.name}
                            </TableCell>
                            <TableCell className="text-center font-mono">
                              {student.exercises_completed}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-mono font-bold ${getMasteryColor(student.avg_accuracy)}`}>
                                {student.avg_accuracy}%
                              </span>
                            </TableCell>
                            <TableCell className="text-center font-mono text-secondary">
                              {student.avg_wpm}
                            </TableCell>
                            <TableCell className="text-center font-mono text-text-muted">
                              {student.total_words_typed.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ObjectivesPage;
